import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  FileText, Search, RefreshCw, Loader2, Clock,
  AlertCircle, CheckCircle, Info, AlertTriangle, 
  ChevronLeft, ChevronRight, X, Copy, ExternalLink,
  Calendar, User, Server, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_OPTIONS = [
  { value: 'all', label: 'Tüm Servisler' },
  { value: 'system', label: 'Sistem' },
  { value: 'api', label: 'API' },
  { value: 'auth', label: 'Kimlik Doğrulama' },
  { value: 'payments', label: 'Ödemeler' },
  { value: 'notifications', label: 'Bildirimler' },
  { value: 'worker', label: 'Worker' },
  { value: 'database', label: 'Veritabanı' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'strategies', label: 'Stratejiler' },
  { value: 'analytics', label: 'Analytics' },
];

const LEVEL_OPTIONS = [
  { value: 'all', label: 'Tüm Seviyeler' },
  { value: 'error', label: 'Hata', color: 'text-red-400' },
  { value: 'warning', label: 'Uyarı', color: 'text-yellow-400' },
  { value: 'info', label: 'Bilgi', color: 'text-blue-400' },
  { value: 'success', label: 'Başarılı', color: 'text-green-400' },
];

const AdminLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State from URL params
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, by_level: {}, by_service: {} });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 1 });
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filterLevel, setFilterLevel] = useState(searchParams.get('level') || 'all');
  const [filterService, setFilterService] = useState(searchParams.get('service') || 'all');
  const [fromDate, setFromDate] = useState(searchParams.get('from') || '');
  const [toDate, setToDate] = useState(searchParams.get('to') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  
  // Detail modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Build query params and update URL
  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (searchQuery) params.set('q', searchQuery);
    if (filterLevel !== 'all') params.set('level', filterLevel);
    if (filterService !== 'all') params.set('service', filterService);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    return params;
  }, [searchQuery, filterLevel, filterService, fromDate, toDate, currentPage]);

  // Fetch logs from API
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      
      if (searchQuery) params.set('q', searchQuery);
      if (filterLevel !== 'all') params.set('level', filterLevel);
      if (filterService !== 'all') params.set('service', filterService);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      params.set('page', currentPage.toString());
      params.set('limit', '50');
      
      const response = await axios.get(`/admin/logs?${params.toString()}`);
      
      setLogs(response.data.logs || []);
      setStats(response.data.stats || { total: 0, by_level: {}, by_service: {} });
      setPagination({
        page: response.data.page || 1,
        limit: response.data.limit || 50,
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 1
      });
      
    } catch (error) {
      console.error('Logs fetch error:', error);
      toast.error('Loglar yüklenirken hata oluştu');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterLevel, filterService, fromDate, toDate, currentPage]);

  // Update URL when filters change
  useEffect(() => {
    const params = buildQueryParams();
    setSearchParams(params, { replace: true });
  }, [buildQueryParams, setSearchParams]);

  // Fetch logs when filters change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchLogs();
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [fetchLogs]);

  // Reset to page 1 when filters change
  const handleFilterChange = (setter, value) => {
    setter(value);
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error': return <AlertCircle className="h-4 w-4 text-red-400" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
      case 'success': return <CheckCircle className="h-4 w-4 text-green-400" />;
      default: return <Info className="h-4 w-4 text-blue-400" />;
    }
  };

  const getLevelBadge = (level) => {
    switch (level?.toLowerCase()) {
      case 'error': return <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Error</Badge>;
      case 'warning': return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Warning</Badge>;
      case 'success': return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Success</Badge>;
      default: return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Info</Badge>;
    }
  };

  const getServiceBadge = (service) => {
    const colors = {
      system: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
      api: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
      auth: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      payments: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
      notifications: 'bg-pink-500/20 text-pink-400 border-pink-500/50',
      database: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50',
      default: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
    };
    
    return (
      <Badge className={colors[service?.toLowerCase()] || colors.default}>
        {service || 'unknown'}
      </Badge>
    );
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Panoya kopyalandı');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterLevel('all');
    setFilterService('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || filterLevel !== 'all' || filterService !== 'all' || fromDate || toDate;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            Sistem Logları
          </h1>
          <p className="text-slate-400 text-sm">Sistem aktivitelerini ve hataları görüntüle</p>
        </div>
        <div className="flex gap-2">
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" />
              Filtreleri Temizle
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/30 border-slate-700/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Toplam Log</p>
                <p className="text-2xl font-bold text-white">{stats.total || pagination.total}</p>
              </div>
              <FileText className="h-8 w-8 text-slate-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Hatalar</p>
                <p className="text-2xl font-bold text-red-400">{stats.by_level?.error || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-yellow-500/5 border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Uyarılar</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.by_level?.warning || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500">Başarılı</p>
                <p className="text-2xl font-bold text-green-400">{stats.by_level?.success || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Mesaj, event veya email ara..."
                value={searchQuery}
                onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)}
                className="pl-10 bg-brand-dark border-border/40"
              />
            </div>

            {/* Level Filter */}
            <Select 
              value={filterLevel} 
              onValueChange={(v) => handleFilterChange(setFilterLevel, v)}
            >
              <SelectTrigger className="bg-brand-dark border-border/40">
                <SelectValue placeholder="Seviye" />
              </SelectTrigger>
              <SelectContent>
                {LEVEL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className={opt.color}>{opt.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Service Filter */}
            <Select 
              value={filterService} 
              onValueChange={(v) => handleFilterChange(setFilterService, v)}
            >
              <SelectTrigger className="bg-brand-dark border-border/40">
                <SelectValue placeholder="Servis" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date Range */}
            <div className="flex gap-2">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => handleFilterChange(setFromDate, e.target.value)}
                className="bg-brand-dark border-border/40 text-sm"
                placeholder="Başlangıç"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            Loglar 
            <span className="text-sm font-normal text-slate-400 ml-2">
              ({pagination.total} kayıt, sayfa {pagination.page}/{pagination.total_pages})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>Log kaydı bulunamadı</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Filtreleri temizle
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div 
                  key={log.id || index} 
                  className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors cursor-pointer"
                  onClick={() => openLogDetail(log)}
                >
                  {getLevelIcon(log.level)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {getLevelBadge(log.level)}
                      {getServiceBadge(log.service)}
                      {log.event_name && (
                        <Badge variant="outline" className="text-xs font-mono">
                          {log.event_name}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2">{log.message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(log.timestamp)}
                      </span>
                      {log.user_email && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {log.user_email}
                        </span>
                      )}
                      {log.request_id && (
                        <span className="flex items-center gap-1 font-mono">
                          <Hash className="h-3 w-3" />
                          {log.request_id.substring(0, 8)}...
                        </span>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-slate-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border/40">
              <div className="text-sm text-slate-500">
                Toplam {pagination.total} kayıt
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Önceki
                </Button>
                <span className="text-sm text-slate-400 px-3">
                  {currentPage} / {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={currentPage >= pagination.total_pages}
                >
                  Sonraki
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl bg-brand-card border-border/40">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getLevelIcon(selectedLog.level)}
              Log Detayı
            </DialogTitle>
            <DialogDescription>
              {selectedLog?.event_name || 'Sistem logu'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {getLevelBadge(selectedLog.level)}
                {getServiceBadge(selectedLog.service)}
                {selectedLog.event_name && (
                  <Badge variant="outline" className="font-mono">{selectedLog.event_name}</Badge>
                )}
              </div>

              {/* Message */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 font-medium">MESAJ</p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => copyToClipboard(selectedLog.message)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-sm text-slate-200">{selectedLog.message}</p>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Zaman</p>
                  <p className="text-sm text-slate-300 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(selectedLog.timestamp)}
                  </p>
                </div>
                {selectedLog.user_email && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Kullanıcı</p>
                    <p className="text-sm text-slate-300 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {selectedLog.user_email}
                    </p>
                  </div>
                )}
                {selectedLog.request_id && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Request ID</p>
                    <p className="text-sm text-slate-300 font-mono flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {selectedLog.request_id}
                    </p>
                  </div>
                )}
                {selectedLog.ip_address && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">IP Adresi</p>
                    <p className="text-sm text-slate-300 font-mono flex items-center gap-1">
                      <Server className="h-3 w-3" />
                      {selectedLog.ip_address}
                    </p>
                  </div>
                )}
              </div>

              {/* Context JSON */}
              {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 font-medium">CONTEXT (JSON)</p>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(JSON.stringify(selectedLog.context, null, 2))}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <pre className="text-xs text-slate-300 overflow-x-auto max-h-60 overflow-y-auto">
                    {JSON.stringify(selectedLog.context, null, 2)}
                  </pre>
                </div>
              )}

              {/* Log ID */}
              <div className="pt-4 border-t border-border/40">
                <p className="text-xs text-slate-500">
                  Log ID: <span className="font-mono">{selectedLog.id}</span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogs;
